import React from 'react';
import type { FilterState, SeedResponse } from '../types';
import ProjectSelect from './ProjectSelect';
import TagFilterBar from './TagFilterBar';

interface CreateBatchModalProps {
  open: boolean;
  loading: boolean;
  seedFilter: FilterState;
  availableTags: string[];
  seeds: SeedResponse[];
  selectedSeedIds: string[];
  name: string;
  runMode: 'sequential' | 'parallel';
  maxParallel: number;
  autorun: boolean;
  repetitionCount: number;
  tagsCsv: string;
  projectId: string | null;
  onProjectChange: (id: string | null) => void;
  onFilterChange: (filter: FilterState) => void;
  onToggleSeed: (seedId: string) => void;
  onLoadMoreSeeds: () => void;
  seedHasMore: boolean;
  seedLoadingMore: boolean;
  seedTotal: number;
  onSelectAllFiltered: () => void;
  selectingAllFiltered: boolean;
  onUnselectAll: () => void;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onFieldChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
}

const CreateBatchModal: React.FC<CreateBatchModalProps> = (props) => {
  const showLargeBatchWarning = props.selectedSeedIds.length > 100;

  const handleSeedsScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!props.seedHasMore || props.seedLoadingMore) {
      return;
    }
    const element = event.currentTarget;
    const nearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 24;
    if (nearBottom) {
      props.onLoadMoreSeeds();
    }
  };

  if (!props.open) return null;
  return (
    <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, marginBottom: 16, border: '1px solid #e0e0e0' }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Create Batch</h3>
      <form onSubmit={props.onSubmit}>
        <div style={{ display: 'grid', gap: 12 }}>
          <input name="name" value={props.name} onChange={props.onFieldChange} placeholder="Batch name" style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
          <input name="tags_csv" value={props.tagsCsv} onChange={props.onFieldChange} placeholder="Tags (comma-separated)" style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }} />
          <ProjectSelect value={props.projectId} onChange={props.onProjectChange} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <select name="run_mode" value={props.runMode} onChange={props.onFieldChange}><option value="sequential">sequential</option><option value="parallel">parallel</option></select>
            <input name="max_parallel" type="number" min={1} max={100} value={props.maxParallel} onChange={props.onFieldChange} disabled={props.runMode !== 'parallel'} placeholder="max parallel" />
            <input name="repetition_count" type="number" min={1} max={50} value={props.repetitionCount} onChange={props.onFieldChange} placeholder="repetitions" title="Repetitions per seed (1-50)" />
          </div>
          <label><input name="autorun" type="checkbox" checked={props.autorun} onChange={props.onFieldChange} /> Autorun</label>
          <TagFilterBar tags={props.availableTags} value={props.seedFilter} onChange={props.onFilterChange} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 12, color: '#555' }}>
              Selected {props.selectedSeedIds.length} of {props.seedTotal} filtered seeds
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="button"
                onClick={props.onSelectAllFiltered}
                disabled={props.selectingAllFiltered}
                style={{ padding: '4px 8px', fontSize: 12 }}
              >
                {props.selectingAllFiltered ? 'Selecting...' : 'Select all filtered'}
              </button>
              <button
                type="button"
                className="button"
                onClick={props.onUnselectAll}
                disabled={props.selectedSeedIds.length === 0}
                style={{ padding: '4px 8px', fontSize: 12, background: '#e0e0e0' }}
              >
                Unselect all
              </button>
            </div>
          </div>
          {showLargeBatchWarning && (
            <div
              style={{
                padding: 8,
                borderRadius: 6,
                background: '#fff3cd',
                color: '#856404',
                border: '1px solid #ffeeba',
                fontSize: 12,
              }}
            >
              Warning: this batch contains more than 100 seeds and can take significantly longer to run.
            </div>
          )}
          <div
            style={{ maxHeight: 240, overflow: 'auto', border: '1px solid #eee', borderRadius: 6, padding: 8 }}
            onScroll={handleSeedsScroll}
          >
            {props.seeds.map((seed) => (
              <label key={seed.seed_id} style={{ display: 'block', padding: '4px 0' }}>
                <input
                  type="checkbox"
                  checked={props.selectedSeedIds.includes(seed.seed_id)}
                  onChange={() => props.onToggleSeed(seed.seed_id)}
                />{' '}
                {seed.name}
              </label>
            ))}
            {props.seedLoadingMore && (
              <div style={{ padding: '6px 0', fontSize: 12, color: '#666' }}>Loading more seeds...</div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="button" onClick={props.onClose}>Cancel</button>
            <button type="submit" className="button button-primary" disabled={props.loading}>Create</button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateBatchModal;
